import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiCall from '../../../api';
import { toast } from 'react-toastify';
import { setItem } from '../../helper/localstorage.helper';
import { updateUser as updateAuthUser, logOut } from "../../../redux/auth/authSlice";


interface BankIDState {
    bankDetails: {
        id?: string;
        autoStartToken?: string;
    };
    qrCodeData: {
        id?: string;
        qrCode?: string;
        autoStartToken?: string;
    };
    isInitializing: boolean;
    isAuthenticating: boolean;
    isQRModalOpen: boolean;
    qrState: {
        isLoading: boolean;
        isAuthenticated: boolean;
        customMessage: string;
    };
    isAuthSuccessful: boolean;
    userData: any;
    error: string | null;
    message: string | null;
    sessionTimeout: boolean;
    needsReload: boolean;
    toggleReactivate: boolean;
    inactiveAccount: boolean;
}

const initialState: BankIDState = {
    bankDetails: {},
    qrCodeData: {},
    isInitializing: false,
    isAuthenticating: false,
    isQRModalOpen: false,
    qrState: {
        isLoading: false,
        isAuthenticated: false,
        customMessage: "SCAN_QR_FROM_BANK_APP",
    },
    isAuthSuccessful: false,
    userData: null,
    error: null,
    message: null,
    sessionTimeout: false,
    needsReload: false,
    toggleReactivate: false,
    inactiveAccount: false,
};

export const initializeBankIDDetails = createAsyncThunk(
    'bankid/initializeBankIDDetails',
    async (_, { rejectWithValue }) => {
        try {
            const response = await apiCall.get('/auth');
            if (response?.status === 200) {
                return response.data.response;
            }
            throw new Error('Failed to initialize BankID details');
        } catch (error: any) {
            return rejectWithValue(
                error?.response?.data?.message || 'Failed to get bank details'
            );
        }
    }
);

export const initializeBankIDFlow = createAsyncThunk(
    'bankid/initializeBankIDFlow',
    async (_, { rejectWithValue }) => {
        try {
            const response = await apiCall.get('/auth');
            if (response?.status === 200) {
                return response.data.response;
            }
            throw new Error('Failed to initialize BankID flow');
        } catch (error: any) {
            return rejectWithValue(
                error?.response?.data?.message || 'Failed to generate QR code'
            );
        }
    }
);

export const checkBankIDAuthStatus = createAsyncThunk(
    'bankid/checkBankIDAuthStatus',
    async (
        { bankIdSession, mode }: { bankIdSession: string; mode: 'login' | 'signup' },
        { rejectWithValue, dispatch }
    ) => {
        try {
            const endpoint = `/auth/collect-response/qr-code/${bankIdSession}`;
            let response;

            if (mode === 'signup') {
                response = await apiCall.post(endpoint);
            } else {
                response = await apiCall.get(endpoint);
            }
            if (response.data.message === 'SESSION_TIMEOUT') {
                dispatch(setSessionTimeout(true));
                return response.data;
            }

            if (response?.status === 200) {
                const result = {
                    ...response.data,
                    mode,
                };

                if (result.response?.token && result.response?.userData) {
                    dispatch(updateAuthUser(result.response.userData));
                }

                return result;
            }

            throw new Error('Authentication check failed');
        } catch (error: any) {
            return rejectWithValue(
                error?.response?.data?.message || 'Authentication failed'
            );
        }
    }
);

export const authenticateWithBankIDSameDevice = createAsyncThunk(
    'bankid/authenticateWithBankIDSameDevice',
    async (
        {
            mode,
            accountType,
            language,
        }: {
            mode: 'login' | 'signup';
            accountType?: 'freelancer' | 'business';
            language?: string;
        },
        { getState, rejectWithValue }
    ) => {
        try {
            const state = getState() as { bankidAuthFlow: BankIDState };
            const { bankDetails } = state.bankidAuthFlow;

            if (!bankDetails?.id || !bankDetails?.autoStartToken) {
                throw new Error('Bank details not available');
            }

            const baseUrl = (import.meta as any).env.VITE_API_URL || 'https://fakturanu-api-services.azurewebsites.net/api';
            const redirectParams = new URLSearchParams({
                lang: language || 'sv-se',
            });

            if (mode === 'signup') {
                redirectParams.set('signup', 'true');
                if (accountType) {
                    redirectParams.set('accountType', accountType === 'business' ? '2' : '1');
                }
            }

            const url1 = `${baseUrl}/auth/collect-response/${bankDetails.id}?${redirectParams.toString()}`;
            const encoded = encodeURIComponent(url1);
            const url = `https://app.bankid.com/?autostarttoken=${bankDetails.autoStartToken}&redirect=${encoded}`;

            window.open(url, '_blank');

            return { success: true, method: 'same-device' };
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to open BankID app');
        }
    }
);
const bankIDSlice = createSlice({
    name: 'bankid',
    initialState,
    reducers: {
        setQRModalOpen: (state, action) => {
            state.isQRModalOpen = action.payload;
        },

        setQRState: (state, action) => {
            state.qrState = { ...state.qrState, ...action.payload };
        },

        setSessionTimeout: (state, action) => {
            state.sessionTimeout = action.payload;
            if (action.payload) {
                state.qrState.customMessage = 'Your session has expired due to inactivity, Please try again';
                state.needsReload = true;
            }
        },

        setNeedsReload: (state, action) => {
            state.needsReload = action.payload;
            if (!action.payload) {
                state.qrState = {
                    isLoading: true,
                    isAuthenticated: false,
                    customMessage: "SCAN_QR_FROM_BANK_APP",
                };
                state.sessionTimeout = false;
            }
        },

        setAuthSuccessful: (state, action) => {
            state.isAuthSuccessful = action.payload;
        },

        resetBankIDFlow: (state) => {
            state.qrCodeData = {};
            state.qrState = {
                isLoading: false,
                isAuthenticated: false,
                customMessage: "SCAN_QR_FROM_BANK_APP",
            };
            state.isQRModalOpen = false;
            state.isAuthSuccessful = false;
            state.error = null;
            state.message = null;
            state.sessionTimeout = false;
            state.needsReload = false;
        },

        clearQRData: (state) => {
            state.qrCodeData = {};
            state.qrState = {
                isLoading: true,
                isAuthenticated: false,
                customMessage: 'Scan QR from your bank\'s mobile application',
            };
            state.sessionTimeout = false;
            state.needsReload = false;
            state.error = null;
        },

        resetBankIDState: () => initialState,

        clearError: (state) => {
            state.error = null;
        },

        setCustomMessage: (state, action) => {
            state.qrState.customMessage = action.payload;
        },

        // Account reactivation actions
        setToggleReactivate: (state, action) => {
            state.toggleReactivate = action.payload;
        },

        setInactiveAccount: (state, action) => {
            state.inactiveAccount = action.payload;
        },

        // Clear state on signout
        clearBankIDStateOnSignout: () => initialState,
        handleReactivationSuccess: (state) => {
            state.inactiveAccount = false;
            state.toggleReactivate = false;
            state.error = null;
            state.qrCodeData = {};
            state.bankDetails = {};
            state.qrState = {
                isLoading: false,
                isAuthenticated: false,
                customMessage: "SCAN_QR_FROM_BANK_APP",
            };
        },
    },

    extraReducers: (builder) => {
        builder
            .addCase(initializeBankIDDetails.pending, (state) => {
                state.isInitializing = true;
                state.error = null;
            })
            .addCase(initializeBankIDDetails.fulfilled, (state, action) => {
                state.isInitializing = false;
                state.bankDetails = action.payload;
            })
            .addCase(initializeBankIDDetails.rejected, (state, action) => {
                state.isInitializing = false;
                state.error = action.payload as string;
            });

        builder
            .addCase(initializeBankIDFlow.pending, (state) => {
                state.qrState.isLoading = true;
                state.error = null;
            })
            .addCase(initializeBankIDFlow.fulfilled, (state, action) => {
                state.qrState.isLoading = false;
                state.qrCodeData = action.payload;
            })
            .addCase(initializeBankIDFlow.rejected, (state, action) => {
                state.qrState.isLoading = false;
                state.error = action.payload as string;
                state.qrState.customMessage = action.payload as string || 'Failed to generate QR code';
            });

        builder
            .addCase(checkBankIDAuthStatus.pending, (state) => {
                state.isAuthenticating = true;
            })
            .addCase(checkBankIDAuthStatus.fulfilled, (state, action) => {
                state.isAuthenticating = false;
                if (!action?.payload) return;
                const { response, message, mode } = action.payload;
                if (response?.token) {
                    state.qrState.isAuthenticated = true;
                    state.isAuthSuccessful = true;
                    state.userData = response;
                    setItem('user', JSON.stringify(response));

                } else {
                    if (typeof message === 'string' && message.includes("We couldn’t find an account with this SSN")) {
                        state.needsReload = true;
                        state.qrState.customMessage = "SSN_DOESNT_EXIST";
                    } else if (message === 'SESSION_TIMEOUT') {
                        state.sessionTimeout = true;
                        state.needsReload = true;
                        state.userData = initialState;
                        state.qrState.customMessage = 'BANKID_SESSION_EXPIRED';
                    } else if (message === 'account_deactivated' || message === 'inactive_account') {
                        if (response?.userData) {
                            state.userData = response.userData;
                        } else if (response) {
                            state.userData = response;
                        }
                        state.isQRModalOpen = false;
                        state.needsReload = true;
                        state.error = message;
                        state.inactiveAccount = message === 'inactive_account';
                        state.toggleReactivate = true;
                    } else if (mode === 'signup' && message === 'User already registerd') {
                        toast.error('Account with this BankID already exists. Please login instead.');
                        state.isQRModalOpen = false;
                        state.error = message;
                    } else {
                        state.qrState.customMessage = message || 'Please check your BankID app';
                    }
                }
            })
            .addCase(checkBankIDAuthStatus.rejected, (state, action) => {
                state.isAuthenticating = false;
                state.error = action.payload as string;
                state.qrState.customMessage = action.payload as string || 'Authentication failed';
            });

        builder
            .addCase(authenticateWithBankIDSameDevice.pending, (state) => {
                state.isInitializing = true;
            })
            .addCase(authenticateWithBankIDSameDevice.fulfilled, (state) => {
                state.isInitializing = false;
            })
            .addCase(authenticateWithBankIDSameDevice.rejected, (state, action) => {
                state.isInitializing = false;
                state.error = action.payload as string;
                toast.error(action.payload as string);
            });

        builder
            .addCase(logOut, () => initialState);
    },
});

export const {
    setQRModalOpen,
    setQRState,
    setSessionTimeout,
    setNeedsReload,
    setAuthSuccessful,
    resetBankIDFlow,
    clearQRData,
    resetBankIDState,
    clearError,
    setCustomMessage,
    setToggleReactivate,
    setInactiveAccount,
    clearBankIDStateOnSignout,
    handleReactivationSuccess,
} = bankIDSlice.actions;

export default bankIDSlice.reducer;
